import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { lucideSearch, lucideX } from '@ng-icons/lucide';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'au-table-search',
  imports: [ReactiveFormsModule, NgIcon, HlmInputImports, TranslocoDirective],
  providers: [provideIcons({ lucideSearch, lucideX })],
  host: { class: 'block' },
  template: `
    <div class="relative w-full md:w-80" *transloco="let t">
      <ng-icon
        name="lucideSearch"
        class="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 text-sm"
      />
      <input
        #searchInput
        class="w-full pr-9 pl-9"
        [placeholder]="placeholder()"
        [formControl]="searchControl"
        hlmInput
      />
      @if (query()) {
        <button
          class="bg-muted-foreground/30 text-foreground hover:bg-muted-foreground/50 absolute top-1/2 right-3 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-xs transition-colors"
          type="button"
          [attr.aria-label]="t('ClearSearch')"
          (click)="clear()"
        >
          <ng-icon name="lucideX" />
        </button>
      }
    </div>
  `,
})
export class TableSearch implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly placeholder = input('Filter...');
  readonly debounce = input(300);
  /**
   * Optional controlled value. When provided, the internal `FormControl`
   * mirrors it (with `emitEvent: false` to avoid feedback loops). Useful when
   * the host owns the search state via a `useTableSearch` atom and needs to
   * reset/preset the input programmatically.
   */
  readonly value = input<string | null | undefined>(undefined);
  readonly search = output<string>();

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  // Undebounced mirror of the input: the clear button must react on the first keystroke.
  protected readonly query = signal('');
  private readonly searchInput = viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  constructor() {
    // Sync external value → internal FormControl without re-emitting.
    effect(() => {
      const next = this.value();
      if (next === undefined || next === null) return;
      if (this.searchControl.value === next) return;
      this.searchControl.setValue(next, { emitEvent: false });
      this.query.set(next);
    });
  }

  ngOnInit() {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.query.set(value));

    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.debounce()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => this.search.emit(value));
  }

  protected clear(): void {
    this.searchControl.setValue('');
    this.searchInput().nativeElement.focus();
  }
}
